export const convertToCardNumber = (number) => {
    return number.toString().replace(/(\d{4})(?=\d)/g, '$1-');
};
export const convertCardToNumber = (card) => {
    return card.replace(/-/g, '');
};
export const formatNumberWithCommas = (number) => {
    const numStr = number.toString();
    const [integerPart, decimalPart] = numStr.split('.');
    // Add commas only to the integer part
    const integerWithCommas = integerPart?.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    // Return with decimal part if it exists
    return decimalPart !== undefined
        ? `${integerWithCommas}.${decimalPart}`
        : integerWithCommas || '';
};
export const formatNumberWithCommasAndDecimals = (number, decimals = 2) => {
    const num = typeof number === 'string' ? parseFloat(number) : number;
    if (isNaN(num))
        return '0';
    const formatted = num.toFixed(decimals);
    const [integerPart, decimalPart] = formatted.split('.');
    // Add commas to integer part
    const integerWithCommas = integerPart?.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    if (!decimalPart)
        return integerWithCommas || '0';
    // Remove trailing zeros
    const trimmedDecimalPart = decimalPart.replace(/0+$/, '');
    // Return with decimal part only if it's not empty after trimming
    const temp = trimmedDecimalPart
        ? `${integerWithCommas}.${trimmedDecimalPart}`
        : integerWithCommas || '0';
    return temp;
};
export const removeCommasFromNumber = (str) => {
    return str.replace(/,/g, '');
};
export function convertNumberToLetterEnglish(num) {
    const ones = [
        '',
        'one',
        'two',
        'three',
        'four',
        'five',
        'six',
        'seven',
        'eight',
        'nine',
    ];
    const teens = [
        'ten',
        'eleven',
        'twelve',
        'thirteen',
        'fourteen',
        'fifteen',
        'sixteen',
        'seventeen',
        'eighteen',
        'nineteen',
    ];
    const tens = [
        '',
        '',
        'twenty',
        'thirty',
        'forty',
        'fifty',
        'sixty',
        'seventy',
        'eighty',
        'ninety',
    ];
    const scales = ['', 'thousand', 'million', 'billion', 'trillion'];
    if (num === 0)
        return 'zero';
    function convertLessThanThousand(n) {
        if (n === 0)
            return '';
        if (n < 10)
            return ones[n] || '';
        if (n < 20)
            return teens[n - 10] || '';
        if (n < 100) {
            return ((tens[Math.floor(n / 10)] || '') +
                (n % 10 !== 0 ? ' ' + (ones[n % 10] || '') : ''));
        }
        return (ones[Math.floor(n / 100)] +
            ' hundred' +
            (n % 100 !== 0 ? ' and ' + convertLessThanThousand(n % 100) : ''));
    }
    function convert(n, scaleIndex) {
        if (n === 0)
            return '';
        const chunk = n % 1000;
        const rest = Math.floor(n / 1000);
        const chunkWords = convertLessThanThousand(chunk);
        const scale = chunk !== 0 ? ' ' + scales[scaleIndex] : '';
        return (convert(rest, scaleIndex + 1) +
            (chunkWords ? ' ' + chunkWords + scale : ''));
    }
    return convert(num, 0).trim();
}
export function convertNumberToLetterPersian(num) {
    // Check if the number has decimal part
    const numStr = num.toString();
    const hasDecimal = numStr.includes('.');
    if (hasDecimal) {
        const [integerPart] = numStr.split('.');
        const integerNumber = parseInt(integerPart || '0', 10);
        // Convert only the integer part to letters, ignore decimal completely
        return convertIntegerToPersian(integerNumber);
    }
    // If no decimal, convert the whole number
    return convertIntegerToPersian(num);
}
function convertIntegerToPersian(num) {
    const ones = ['', 'یک', 'دو', 'سه', 'چهار', 'پنج', 'شش', 'هفت', 'هشت', 'نه'];
    const teens = [
        'ده',
        'یازده',
        'دوازده',
        'سیزده',
        'چهارده',
        'پانزده',
        'شانزده',
        'هفده',
        'هجده',
        'نوزده',
    ];
    const tens = [
        '',
        '',
        'بیست',
        'سی',
        'چهل',
        'پنجاه',
        'شصت',
        'هفتاد',
        'هشتاد',
        'نود',
    ];
    const hundreds = [
        '',
        'صد',
        'دویست',
        'سیصد',
        'چهارصد',
        'پانصد',
        'ششصد',
        'هفتصد',
        'هشتصد',
        'نهصد',
    ];
    const scales = ['', 'هزار', 'میلیون', 'میلیارد', 'تریلیون'];
    if (num === 0)
        return 'صفر';
    function convertLessThanThousand(n) {
        if (n === 0)
            return '';
        if (n < 10)
            return ones[n] || '';
        if (n < 20)
            return teens[n - 10] || '';
        if (n < 100) {
            const tenDigit = Math.floor(n / 10);
            const oneDigit = n % 10;
            if (oneDigit === 0) {
                return tens[tenDigit] || '';
            }
            else {
                return tens[tenDigit] + ' و ' + ones[oneDigit] || '';
            }
        }
        const hundredDigit = Math.floor(n / 100);
        const remainder = n % 100;
        if (remainder === 0) {
            return hundreds[hundredDigit] || '';
        }
        else {
            return (hundreds[hundredDigit] + ' و ' + convertLessThanThousand(remainder) ||
                '');
        }
    }
    function convert(n, scaleIndex) {
        if (n === 0)
            return '';
        const chunk = n % 1000;
        const rest = Math.floor(n / 1000);
        const chunkWords = convertLessThanThousand(chunk);
        const scale = chunk !== 0 ? ' ' + scales[scaleIndex] : '';
        const restWords = convert(rest, scaleIndex + 1);
        if (restWords && chunkWords) {
            return restWords + ' و ' + chunkWords + scale;
        }
        else if (chunkWords) {
            return chunkWords + scale;
        }
        else {
            return restWords;
        }
    }
    return convert(num, 0).trim();
}
export const convertNumberToLetter = (num, language) => {
    return language === 'fa'
        ? convertNumberToLetterPersian(num)
        : convertNumberToLetterEnglish(num);
};
